import { useQuery } from '@apollo/client';
import { createContext, useState } from 'react';
import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import { downloadXlsx } from '../../../../components/Downloads/helpers';
import GraphControls from '../../../../components/Graphs/GraphControls';
import { GHG_GOOGLE_ANALYTICS_TAG } from '../../constants';
import { GRAPH_ASSETS_QUERY_NEW } from '../operations';
import {
  ASSET_AGGREGATE,
  COMPANY_AGGREGATE,
  CURRENT_YEAR_EMISSIONS,
  graphTitles,
  SCOPE_1_AND_2_SOURCE_TYPES,
  SCOPE_3_SOURCE_TYPES,
  TREE_MAP_AGGREGATES,
  TREE_MAP_TYPES,
} from '../utils';

const TreemapContext = createContext();

export const TreemapProvider = ({ children, portfolios }) => {
  const [openControlsModal, setOpenControlsModal] = useState(false);
  const { ghgScope3 } = useGetFeatureFlags();
  const DEFAULT_EMISSION_TYPES = ghgScope3
    ? [...SCOPE_1_AND_2_SOURCE_TYPES, ...SCOPE_3_SOURCE_TYPES]
    : [...SCOPE_1_AND_2_SOURCE_TYPES];

  const [aggregate, setAggregate] = useState({
    'Layer One': COMPANY_AGGREGATE.name,
    'Layer Two': ASSET_AGGREGATE.name,
    'Layer Three': '',
  });
  const [graphType, setGraphType] = useState(CURRENT_YEAR_EMISSIONS);
  const [emissionsSource, setEmissionsSource] = useState([...SCOPE_1_AND_2_SOURCE_TYPES]);
  const [zoomedNode, setZoomedNode] = useState('');
  // TODO: permanently replace old queries with new ones
  const { data, loading } = useQuery(GRAPH_ASSETS_QUERY_NEW, {
    variables: {
      aggregates: Object.values(aggregate).filter(a => a !== ''),
      chartType: 'treemap',
      emissionsSource,
      graphType: graphType.value,
      portfolios,
    },
  });

  const downloadCallback = (errorHandler, successHandler) => {
    const path = 'portfolio-analysis/export-asset-treemap-data/';
    const body = {
      emissionsSource,
      graphType: graphType.value,
      portfolios,
    };
    const downloadName = `${portfolios[0].name}_Asset_${graphType.label}_Data.xlsx`;

    downloadXlsx(path, body, downloadName, errorHandler, successHandler);
  };

  const onClick = ({ points }) => {
    const node = points[0];
    if (node && node.id) {
      setZoomedNode(node.id);
    } else {
      setZoomedNode('');
    }
  };

  const title = `${graphType.label} ${graphTitles[graphType.value]}`;

  return (
    <TreemapContext.Provider
      value={{
        title,
        setOpenControlsModal,
        emissionsSource,
        labels: data?.graphAssetsNew?.analytics[0].labels || [],
        parents: data?.graphAssetsNew?.analytics[0].parents || [],
        values: data?.graphAssetsNew?.analytics[0].values || [],
        customdata: data?.graphAssetsNew?.analytics[0].customdata || [],
        ids: data?.graphAssetsNew?.analytics[0].ids || [],
        hovertemplate: data?.graphAssetsNew?.analytics[0].hovertemplate || '',
        texttemplate: data?.graphAssetsNew?.analytics[0].texttemplate || [],
        loading,
        downloadCallback,
        onClick,
        zoomedNode,
        googleAnalyticsTag: GHG_GOOGLE_ANALYTICS_TAG,
        message: graphType.message,
      }}
    >
      {children}
      <GraphControls
        aggregate={aggregate}
        aggregateTypes={TREE_MAP_AGGREGATES}
        aggregateLayers={Object.keys(aggregate)}
        googleAnalyticsTag={GHG_GOOGLE_ANALYTICS_TAG}
        graphType={graphType}
        emissionSourceTypes={DEFAULT_EMISSION_TYPES}
        emissionsSource={emissionsSource}
        open={openControlsModal}
        setAggregate={setAggregate}
        setGraphType={setGraphType}
        setEmissionsSource={setEmissionsSource}
        setOpen={setOpenControlsModal}
        types={TREE_MAP_TYPES}
      />
    </TreemapContext.Provider>
  );
};

export const TreemapConsumer = TreemapContext.Consumer;

export default TreemapContext;
